import { useState, useCallback, useRef } from 'react';
import { type ZodType, ZodError } from 'zod';

export function useFormValidation<T>(schema: ZodType<T>) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Ref so callers can read the latest errors synchronously inside event handlers
  // (React state batching means the state copy in a closure is stale until re-render)
  const errorsRef = useRef<Record<string, string>>({});

  const validate = useCallback(
    (data: unknown): data is T => {
      try {
        schema.parse(data);
        errorsRef.current = {};
        setErrors({});
        return true;
      } catch (err) {
        if (err instanceof ZodError) {
          const fieldErrors: Record<string, string> = {};
          for (const issue of err.issues) {
            const path = issue.path.join('.');
            if (!fieldErrors[path]) {
              fieldErrors[path] = issue.message;
            }
          }
          errorsRef.current = fieldErrors;
          setErrors(fieldErrors);
        }
        return false;
      }
    },
    [schema]
  );

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      errorsRef.current = next;
      return next;
    });
  }, []);

  const clearErrors = useCallback(() => {
    errorsRef.current = {};
    setErrors({});
  }, []);

  return { errors, errorsRef, validate, clearFieldError, clearErrors };
}
